import axios from 'axios'
import store from '@/store'
import i18n from '@/i18n'
import { captureLog } from '@/helpers/log'
import { getRootDomain } from '@/helperMethods'
import { post } from '@/api/rest'
import { storage } from '@/helpers/utils'
import { getEnv } from '@/helperMethods'
import { environments } from '@/helpers/constants'

let integrationHttp = null

// DevTools override — lets devs point the app at a locally running
// integrations server (see DevTools > Integrations API). Never honored in prod.
export const DEV_INTEGRATIONS_URL_KEY = 'pronto_dev_integrations_url'

const getIntegrationUrlOverride = () => {
	// Allow-list to match getIntegrationBaseUrl, which treats unknown envs as prod
	const allowedEnvs = [
		'local',
		environments.DEV,
		environments.BETA,
		environments.SANDBOX,
	]
	if (!allowedEnvs.includes(getEnv())) return null

	const override = storage.get(DEV_INTEGRATIONS_URL_KEY)
	if (typeof override !== 'string' || !override.trim()) return null

	return override.trim().replace(/\/+$/, '')
}

// With an override active, requests bypass the local vite proxy and must be
// built the same way as in deployed environments
const isUsingLocalProxy = () =>
	getEnv() === 'local' && !getIntegrationUrlOverride()

export const resetIntegrationInstance = () => {
	integrationHttp = null
}

export const getIntegrationBaseUrl = () => {
	const override = getIntegrationUrlOverride()
	if (override) return override

	const env = getEnv()

	// Only use proxy for local development
	if (env === 'local') {
		return '/integrations'
	}

	// For all deployed environments, construct the base domain based on environment
	// instead of trying to parse the current domain
	let baseDomain
	switch (env) {
		case 'dev':
			baseDomain = 'dev.pronto.io'
			break
		case 'beta':
			baseDomain = 'beta.pronto.io'
			break
		case 'sandbox':
			baseDomain = 'sandbox.pronto.io'
			break
		default:
			baseDomain = 'prod.pronto.io'
			break
	}

	return `https://integrations.${baseDomain}`
}

const isTokenExpiredOrExpiring = (tokenObj, bufferMinutes = 5) => {
	if (!tokenObj || !tokenObj.token || !tokenObj.expires) return true

	const now = new Date()
	const expiresDate = new Date(tokenObj.expires)
	const bufferMs = bufferMinutes * 60 * 1000

	return now >= expiresDate.getTime() - bufferMs
}

const refreshIntegrationToken = async () => {
	const data = await post('/organization/integrations/integration_tokens')

	store.auth.setIntegrationJwt(data, storage.get('userKey'))
	return data
}

const getIntegrationInstance = () => {
	if (integrationHttp) return integrationHttp

	const baseURL = getIntegrationBaseUrl()

	const { buildNumber, device_type } = window.appConfig
	const version = `${device_type || 'WEB'}/${buildNumber || 1}/0`

	// Captured locally so the interceptors keep working on this instance even
	// after resetIntegrationInstance() nulls the module-level cache
	const instance = axios.create({
		baseURL: baseURL,
		headers: {
			'X-Time-Zone': store.timezone,
			'Accept-Language': i18n.global.locale || 'en',
			'X-Version': version,
		},
	})

	// use equest interceptor to get current JWT integrations token from store
	instance.interceptors.request.use(async (config) => {
		let tokenObj = store.auth.integration_jwt

		if (isTokenExpiredOrExpiring(tokenObj)) {
			tokenObj = await refreshIntegrationToken()
		}

		config.headers.Authorization = `Bearer ${tokenObj.token}`
		return config
	})

	// Response interceptor - handle 401s as backup
	instance.interceptors.response.use(
		(response) => response,
		async (error) => {
			if (error.response?.status === 401 && !error.config._retry) {
				error.config._retry = true

				try {
					const newTokenObj = await refreshIntegrationToken()
					error.config.headers.Authorization = `Bearer ${newTokenObj.token}`
					return instance.request(error.config)
				} catch (refreshError) {
					store.auth.removeIntegrationJwt()
					throw refreshError
				}
			}
			throw error
		}
	)

	integrationHttp = instance
	return instance
}

const log = (phase, method, url, data, ...extras) => {
	const fullUrl = `${getIntegrationBaseUrl()}${url}`
	captureLog('Integration API', phase, `${method} ${fullUrl}`, data, ...extras)
}

// Helper function to normalize URL paths based on endpoint requirements
const normalizeIntegrationPath = (url) => {
	// For local development, vite proxy handles all the rewriting - just pass the URL as-is
	if (isUsingLocalProxy()) {
		return url
	}

	// For deployed environments, handle the logic here
	if (url.includes('/lti13/') || url.includes('/google_workspace/')) {
		// These endpoints don't need /integrations prefix
		return url
	}

	// All other endpoints need /integrations prefix
	return `/integrations${url}`
}

const processIntegrationResponse = (url, resp) => {
	const obj = { ...resp.data, url, fullUrl: `${getIntegrationBaseUrl()}${url}` }

	// Handle cursor-based pagination instead of links
	if (resp.data.next_page_url) {
		obj.nextUrl = resp.data.next_page_url
		obj.next = async () => {
			// Extract the path from the full URL and ensure we use the correct base URL
			let path = resp.data.next_page_url
			// Parse the URL to extract just the path + query string
			try {
				const url = new URL(resp.data.next_page_url)
				path = url.pathname + url.search
			} catch (e) {
				// If URL parsing fails, fall back to string replacement
				if (path.startsWith('http')) {
					const urlObj = new URL(path)
					path = urlObj.pathname + urlObj.search
				}
			}

			// Remove /integrations prefix since our baseURL already points to the integrations service
			if (path.startsWith('/integrations')) {
				// Only strip /integrations prefix when the local vite proxy is in use,
				// since that's the only case where baseURL includes it
				if (isUsingLocalProxy()) {
					path = path.replace('/integrations', '')
				}
			}

			return processIntegrationResponse(
				path,
				await getIntegrationInstance().get(path)
			)
		}
	}

	if (resp.data.prev_page_url) {
		obj.prevUrl = resp.data.prev_page_url
		obj.prev = async () => {
			// Extract the path from the full URL and ensure we use the correct base URL
			let path = resp.data.prev_page_url

			// Parse the URL to extract just the path + query string
			try {
				const url = new URL(resp.data.prev_page_url)
				path = url.pathname + url.search
			} catch (e) {
				// If URL parsing fails, fall back to string replacement
				if (path.startsWith('http')) {
					const urlObj = new URL(path)
					path = urlObj.pathname + urlObj.search
				}
			}

			// Remove /integrations prefix since our baseURL already points to the integrations service
			if (path.startsWith('/integrations')) {
				// Only strip /integrations prefix when the local vite proxy is in use,
				// since that's the only case where baseURL includes it
				if (isUsingLocalProxy()) {
					path = path.replace('/integrations', '')
				}
			}

			return processIntegrationResponse(
				path,
				await getIntegrationInstance().get(path)
			)
		}
	}

	return obj
}

export const integrationGet = async (url, params, config = {}) => {
	try {
		const normalizedUrl = normalizeIntegrationPath(url)
		log('init', 'GET', normalizedUrl, params)
		const resp = await getIntegrationInstance().get(normalizedUrl, {
			...config,
			params,
		})
		// log the body only — the full axios response carries the integration
		// JWT in its Authorization header (#1597)
		log('success', 'GET', normalizedUrl, params, resp.data)
		return processIntegrationResponse(normalizedUrl, resp)
	} catch (e) {
		log('error', 'GET', url, params, e.message, e) // Use 'url' instead of 'normalizedUrl'
		throw e
	}
}

export const integrationPost = async (url, body, config) => {
	try {
		const normalizedUrl = normalizeIntegrationPath(url)
		log('init', 'POST', normalizedUrl, body)
		const resp = await getIntegrationInstance().post(
			normalizedUrl,
			body,
			config
		)
		log('success', 'POST', normalizedUrl, body, resp.data)
		return processIntegrationResponse(normalizedUrl, resp)
	} catch (e) {
		log('error', 'POST', url, body, e.message, e) // Use 'url' instead of 'normalizedUrl'
		throw e
	}
}

export const integrationPatch = async (url, body, config) => {
	try {
		const normalizedUrl = normalizeIntegrationPath(url)
		log('init', 'PATCH', normalizedUrl, body)
		const resp = await getIntegrationInstance().patch(
			normalizedUrl,
			body,
			config
		)
		log('success', 'PATCH', normalizedUrl, body, resp.data)
		return processIntegrationResponse(normalizedUrl, resp)
	} catch (e) {
		log('error', 'PATCH', url, body, e.message, e) // Use 'url' instead of 'normalizedUrl'
		throw e
	}
}

export const integrationDelete = async (url, body, config) => {
	try {
		const normalizedUrl = normalizeIntegrationPath(url)
		log('init', 'DELETE', normalizedUrl, body)
		const resp = await getIntegrationInstance().delete(
			normalizedUrl,
			body,
			config
		)
		log('success', 'DELETE', normalizedUrl, body, resp.data)
		return processIntegrationResponse(normalizedUrl, resp)
	} catch (e) {
		log('error', 'DELETE', url, body, e.message, e) // Use 'url' instead of 'normalizedUrl'
		throw e
	}
}

export const integrationPut = async (url, body, config) => {
	try {
		const normalizedUrl = normalizeIntegrationPath(url)
		log('init', 'PUT', normalizedUrl, body)
		const resp = await getIntegrationInstance().put(normalizedUrl, body, config)
		log('success', 'PUT', normalizedUrl, body, resp.data)
		return processIntegrationResponse(normalizedUrl, resp)
	} catch (e) {
		log('error', 'PUT', url, body, e.message, e) // Use 'url' instead of 'normalizedUrl'
		throw e
	}
}
